import React, { useReducer, useState, useEffect, useRef } from "react";
import { FORM_ID } from "../../../constants/Forms";
import {
  insertProfessionalDesignationsIA,
  deleteSingleProfessionalDesignation,
} from "../../../api/makeAPIRequest";
import Moment from "moment";
import {
  pdReducerIA,
  pdInitialStateIA,
} from "./helpers/ProfessionalDesignationsState";
import Pagination from "../../LandingPages/Pagination";
import {
  isAgentEmeritusAndRetiredBrokerContract,
} from "../../../common/Functions";
import { CONTRACT_TYPE_DESCRIPTIONS } from "../../../constants/Contract";
import "../../../assets/stylesheets/StartApplication/CAS/ProfessionalDesignations.scss";
import InputMask from "react-input-mask";
import NavigationButtons from "../../SharedComponents/NavigationButtons";
import LostDataModal from "../../SharedComponents/LostDataModal";

function buildFetchedData(profDesCriaData, profDesData, availableDesignations, isCriaOnly) {
  let apiData = profDesCriaData;
  if (Array.isArray(apiData)) {
    apiData = apiData.reduce((acc, obj) => {
      Object.keys(obj).forEach((key) => {
        if (Array.isArray(obj[key])) {
          acc[key] = (acc[key] || []).concat(obj[key]);
        } else {
          acc[key] = obj[key];
        }
      });
      return acc;
    }, {});
  }

  if (isCriaOnly) {
    return {
      registrations: apiData?.registrations || [],
      designations: apiData?.designations || [],
      other_designations: apiData?.other_designations || [],
    };
  }

  const profDesDesignations = Array.isArray(profDesData?.professionalDesignations)
    ? profDesData.professionalDesignations
    : [];

  const available = Array.isArray(availableDesignations) ? availableDesignations : [];

  const DATE_FORMATS = ["YYYY-MM-DD", "MM/DD/YYYY", "MM/YYYY", "YYYY-MM"];

  const pdCriaDesignations = [];
  const pdNonCriaDesignations = [];

  profDesDesignations.forEach((d) => {
    const matched = available.find((a) => a.id === d.profdesignationid);
    const isCria = matched ? matched.isCria : false;

    if (isCria) {
      pdCriaDesignations.push({
        designationdescription: d.designationdescription,
        dateacquired: d.dateacquired || "",
        source: "PD",
        candidatedesignationsid: d.candidatedesignationsid || "",
      });
    } else {
      const rawDate = d.dateacquired || "";
      const formattedDate =
        rawDate && rawDate !== "Invalid date"
          ? Moment(rawDate, DATE_FORMATS, true).isValid()
            ? Moment(rawDate, DATE_FORMATS, true).format("MM/YYYY")
            : ""
          : "";
      pdNonCriaDesignations.push({
        designation: d.designationdescription || "",
        acquiredDate: formattedDate,
        source: "PD",
        candidatedesignationsid: d.candidatedesignationsid || "",
      });
    }
  });

  const iaOnlyDesignations = Array.isArray(apiData?.designations)
    ? apiData.designations.filter((d) => d.source === "IA")
    : [];

  const pdNonCriaNamesLower = new Set(
    pdNonCriaDesignations.map((d) => (d.designation || "").toLowerCase())
  );

  const iaOnlyOtherDesignations = Array.isArray(apiData?.other_designations)
    ? apiData.other_designations.filter((d) => {
        if (d.source !== "IA") return false;
        return !pdNonCriaNamesLower.has((d.designation || "").toLowerCase());
      })
    : [];

  const iaOnlyRegistrations = Array.isArray(apiData?.registrations)
    ? apiData.registrations.filter((d) => d.source === "IA")
    : [];

  return {
    registrations: iaOnlyRegistrations.length > 0
      ? iaOnlyRegistrations
      : apiData?.registrations || [],
    designations: [...pdCriaDesignations, ...iaOnlyDesignations],
    other_designations: [
      ...pdNonCriaDesignations,
      ...iaOnlyOtherDesignations,
    ],
  };
}

const ProfessionalDesignationsInvestmentAdvisor = (props) => {
  const [state, dispatch] = useReducer(pdReducerIA, pdInitialStateIA);

  const [showSaveLoader, setShowSaveLoader] = useState(false);
  const [hasFormChanged, setHasFormChanged] = useState(false);
  const [showBackModal, setShowBackModal] = useState(false);

  const PAGE_SIZE = 5;
  const [otherDesignationsPage, setOtherDesignationsPage] = useState(1);
  const totalOtherDesignations = state.otherDesignations.length;
  const nPages = Math.ceil(totalOtherDesignations / PAGE_SIZE);
  const pageNumbers = Array.from({ length: nPages }, (_, i) => i + 1);

  const handleOtherDesignationsPageChange = (page) => {
    if (page === "nextPage" && otherDesignationsPage < nPages) {
      setOtherDesignationsPage(otherDesignationsPage + 1);
    } else if (page === "lastPage" && otherDesignationsPage > 1) {
      setOtherDesignationsPage(otherDesignationsPage - 1);
    } else if (typeof page === "number") {
      setOtherDesignationsPage(page);
    }
  };

  const initialDataRef = useRef();
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const skipOnLoadRef = useRef(false);
  const baselineCapturedRef = useRef(false);

  const latestPropsRef = useRef({});
  latestPropsRef.current = {
    profDesCriaData: props.profDesCriaData,
    profDesData: props.profDesData,
    availableDesignations: props.availableDesignations,
  };

  const handleAddRow = () => {
    setHasFormChanged(true);
    props.isFormComplete(false);
    props.hasCurrentFormChanged(true);
    dispatch({ type: "addOtherDesignation" });
  };

  function getDateErrorMsg(dateValue) {
    if (!dateValue || dateValue.trim() === "") return "";
    const enteredDate = Moment(dateValue, "MM/YYYY", true);
    if (!enteredDate.isValid()) return "Please enter a valid date";
    const now = Moment();
    const hundredYearsAgo = now.clone().subtract(100, "years").startOf("month");
    const currentMonth = now.startOf("month");
    if (enteredDate.isBefore(hundredYearsAgo)) return "Date cannot be more than 100 years ago";
    if (enteredDate.isAfter(currentMonth)) return "Date cannot be a future date";
    return "";
  }

  const handleDeleteRow = (index) => {
    setHasFormChanged(true);
    props.isFormComplete(false);
    props.hasCurrentFormChanged(true);
    dispatch({ type: "deleteOtherDesignation", index });
  };

  const handleInputChange = (index, field, value) => {
    setHasFormChanged(true);
    dispatch({ type: "updateOtherDesignation", index, field, value });
  };

  useEffect(() => {
    const items = Array.isArray(props.criaDesignationItems)
      ? props.criaDesignationItems
      : [];

    if (items.length === 0) return;

    const registrations = items
      .filter((item) => item.designationtype === "Registration")
      .map((item) => ({
        id: `reg_${item.profdesignationid}`,
        label: item.designationdescription,
        profdesignationid: item.profdesignationid,
      }))
      .sort((a, b) => a.profdesignationid - b.profdesignationid);

    const designations = items
      .filter((item) => item.designationtype === "CRIA")
      .map((item) => {
        const desc = item.designationdescription || "";
        const abbrMatch = desc.match(/\(([^)]+)\)\s*$/);
        if (abbrMatch) {
          const abbr = abbrMatch[1];
          const fullName = desc.replace(/\s*\([^)]+\)\s*$/, "").trim();
          return {
            id: `des_${item.profdesignationid}`,
            label: `${abbr} - ${fullName}`,
            abbr,
            profdesignationid: item.profdesignationid,
          };
        }
        return {
          id: `des_${item.profdesignationid}`,
          label: desc,
          abbr: desc,
          profdesignationid: item.profdesignationid,
        };
      })
      .sort((a, b) => a.abbr.localeCompare(b.abbr));

    const { profDesCriaData, profDesData, availableDesignations } =
      latestPropsRef.current;

    const isCriaOnly = props.contracttypedesc === CONTRACT_TYPE_DESCRIPTIONS.CRIA_ONLY ||
      props.contracttypedesc === CONTRACT_TYPE_DESCRIPTIONS.INVESTMENT_ADVISOR;

    const fetchedData = buildFetchedData(
      profDesCriaData,
      profDesData,
      availableDesignations,
      isCriaOnly
    );

    skipOnLoadRef.current = false;
    baselineCapturedRef.current = false;
    dispatch({ type: "initAndLoad", registrations, designations, fetchedData });
    setHasFormChanged(false);
    setIsInitialLoad(true);
  }, [props.criaDesignationItems]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isInitialLoad) return;
    if (skipOnLoadRef.current) return;

    const isCriaOnly = props.contracttypedesc === CONTRACT_TYPE_DESCRIPTIONS.CRIA_ONLY ||
      props.contracttypedesc === CONTRACT_TYPE_DESCRIPTIONS.INVESTMENT_ADVISOR;

    const fetchedData = buildFetchedData(
      props.profDesCriaData,
      props.profDesData,
      props.availableDesignations,
      isCriaOnly
    );
    baselineCapturedRef.current = false;
    dispatch({ type: "onLoad", fetchedData });
    setHasFormChanged(false);
    setIsInitialLoad(true);
  }, [props.profDesCriaData, props.profDesData]); // eslint-disable-line react-hooks/exhaustive-deps

  const buildUserInputSnapshot = (registrations, designations, otherDesignations) =>
    JSON.stringify({
      registrations: Object.keys(registrations).reduce((acc, id) => {
        acc[id] = { checked: registrations[id]?.checked, date: registrations[id]?.date };
        return acc;
      }, {}),
      designations: Object.keys(designations).reduce((acc, id) => {
        acc[id] = { checked: designations[id]?.checked, date: designations[id]?.date };
        return acc;
      }, {}),
      otherDesignations: otherDesignations.map((row) => ({
        designation: row.designation,
        date: row.date,
      })),
    });

  useEffect(() => {
    if (!isInitialLoad) return;
    // state.isDataFetched becomes true only after the reducer processes
    // initAndLoad/onLoad — guarantees we snapshot the fully populated state.
    if (!state.isDataFetched) return;

    initialDataRef.current = buildUserInputSnapshot(
      state.registrations,
      state.designations,
      state.otherDesignations
    );
    baselineCapturedRef.current = true;
    setIsInitialLoad(false);
  }, [
    state.registrations,
    state.designations,
    state.otherDesignations,
    state.isDataFetched,
    isInitialLoad,
  ]);

  useEffect(() => {
    if (isInitialLoad || !baselineCapturedRef.current) return;

    const currentSnapshot = buildUserInputSnapshot(
      state.registrations,
      state.designations,
      state.otherDesignations
    );
    setHasFormChanged(initialDataRef.current !== currentSnapshot);
  }, [
    state.registrations,
    state.designations,
    state.otherDesignations,
    isInitialLoad,
  ]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const visited = localStorage.getItem("userVisitedProfCriaPage") === "true";
      if (visited && !props.userVisitedProfCriaPage && props.setUserVisitedProfCriaPage) {
        props.setUserVisitedProfCriaPage(true);
      }
    }
    if (typeof window !== "undefined") {
      localStorage.setItem(
        "userVisitedProfCriaPage",
        props.userVisitedProfCriaPage ? "true" : "false"
      );
    }
  }, [
    props.userVisitedProfCriaPage,
    props.setUserVisitedProfCriaPage,
    hasFormChanged,
    props.setProfDesCriaFormChanged,
    state.registrations,
    state.designations,
    state.otherDesignations,
  ]);

  const navigateBack = () => {
    props.profDesCriaData?.length > 0
      ? props.isFormComplete(true)
      : props.isFormComplete(false);
    props.hasCurrentFormChanged(false);
    setHasFormChanged(false);
    if (
      props.contracttypedesc === CONTRACT_TYPE_DESCRIPTIONS.CRIA_ONLY ||
      props.contracttypedesc === CONTRACT_TYPE_DESCRIPTIONS.INVESTMENT_ADVISOR
    ) {
      props.toggleForm(FORM_ID.EDUCATION);
    } else {
      const isCriaAddon = (props.isCRIA && props.contracttypedesc !== CONTRACT_TYPE_DESCRIPTIONS.CRIA_ONLY)
          || (props.isIA && props.contracttypedesc !== CONTRACT_TYPE_DESCRIPTIONS.INVESTMENT_ADVISOR);
      if (isCriaAddon && props.setProfDesComplete) {
        const pdDesignations = Array.isArray(props.profDesData?.professionalDesignations)
          ? props.profDesData.professionalDesignations
          : [];
        if (props.profDesData?.hasprofdesignation === true && pdDesignations.length === 0) {
          props.setProfDesComplete(false);
        }
      }
      props.toggleForm(FORM_ID.PROFESSIONAL_DESIGNATION);
    }
  };

  const handleShowBackModal = () => {
    if (hasFormChanged) {
      setShowBackModal(true);
    } else {
      navigateBack();
    }
  };

  const handleCloseBackModal = () => setShowBackModal(false);

  const handleBackClick = () => {
    handleCloseBackModal();
    navigateBack();
  };

  const handleNext = async () => {
    const hasRegistrationError = Object.values(state.registrations).some((reg) => reg.error);
    const hasDesignationError = Object.values(state.designations).some((des) => des.error);
    const hasOtherDesignationError = state.otherDesignations.some((row) => row.error);

    if (hasRegistrationError || hasDesignationError || hasOtherDesignationError) return;

    const hasPdSourcedRows =
      Object.values(state.designations).some((d) => d.fromPD) ||
      state.otherDesignations.some((row) => row.fromPD);

    if (!hasFormChanged && !hasPdSourcedRows) {
      props.isFormComplete(true);
      props.hasCurrentFormChanged(false);
      if (props.isCRIA || props?.isIA) {
        if (props.updateProfDesCRIACompleteInd) props.updateProfDesCRIACompleteInd(true);
        isAgentEmeritusAndRetiredBrokerContract(props.contracttypedesc)
          ? props.toggleForm(FORM_ID.DIRECT_DEPOSIT)
          : props.toggleForm(FORM_ID.EMPLOYMENT_HISTORY);
      } else {
        props.toggleForm(FORM_ID.REVIEW_SIGN_1);
      }
      return;
    }

    const selectedRegistrations = [];
    const selectedDesignations = [];
    const otherDesignationsData = [];

    Object.keys(state.registrations).forEach((id) => {
      if (state.registrations[id]?.checked || state.registrations[id]?.date) {
        let cleanedDate = (state.registrations[id]?.date || "").replace(/_/g, "").trim();
        selectedRegistrations.push({
          state: state.registrations[id]?.label,
          date: cleanedDate,
          source: "IA",
        });
      }
    });

    Object.keys(state.designations).forEach((id) => {
      if (state.designations[id]?.checked || state.designations[id]?.date) {
        let cleanedDate = (state.designations[id]?.date || "").replace(/_/g, "").trim();
        const isPdSourced = state.designations[id]?.fromPD;
        selectedDesignations.push({
          state: state.designations[id]?.label,
          date: cleanedDate,
          source: isPdSourced ? "PD" : "IA",
          ...(isPdSourced && state.designations[id]?.candidatedesignationsid !== ""
            ? { candidatedesignationsid: state.designations[id].candidatedesignationsid }
            : {}),
        });
      }
    });

    state.otherDesignations.forEach((row) => {
      let cleanedDate = (row.date || "").replace(/_/g, "").trim();
      if (row.designation.trim() || cleanedDate !== "") {
        otherDesignationsData.push({
          designation: row.designation,
          acquiredDate: cleanedDate,
          source: row.fromPD ? "PD" : "IA",
          ...(row.fromPD && row.candidatedesignationsid !== ""
            ? { candidatedesignationsid: row.candidatedesignationsid }
            : {}),
        });
      }
    });

    const payload = {
      candidateid: props.candidateId,
      contractid: props.contractid,
      registrations: selectedRegistrations,
      designations: selectedDesignations,
      other_designations: otherDesignationsData,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    const pdDesignations = Array.isArray(props.profDesData?.professionalDesignations)
      ? props.profDesData.professionalDesignations
      : [];

    const pdCriaItems = pdDesignations.filter((d) => {
      const matched = (props.availableDesignations || []).find((a) => a.id === d.profdesignationid);
      return matched?.isCria === true;
    });

    const pdNonCriaItems = pdDesignations.filter((d) => {
      const matched = (props.availableDesignations || []).find((a) => a.id === d.profdesignationid);
      return matched?.isCria === false;
    });

    const section2Deleted = [];
    const section3Deleted = [];

    const isCriaOnly = props.contracttypedesc === CONTRACT_TYPE_DESCRIPTIONS.CRIA_ONLY ||
      props.contracttypedesc === CONTRACT_TYPE_DESCRIPTIONS.INVESTMENT_ADVISOR;

    if (isCriaOnly) {
      Object.keys(state.designations).forEach((id) => {
        const entry = state.designations[id];
        if (entry?.fromPD && !entry.checked && entry.candidatedesignationsid !== "") {
          section2Deleted.push({
            candidatedesignationsid: entry.candidatedesignationsid,
            designationdescription: entry.label,
          });
        }
      });

      const currentOtherRows = state.otherDesignations.filter((row) =>
        (row.designation || "").trim()
      );
      const currentOtherNamesLower = new Set(
        currentOtherRows.map((r) => r.designation.trim().toLowerCase())
      );

      const originalIAData = Array.isArray(props.profDesCriaData)
        ? props.profDesCriaData[0]
        : props.profDesCriaData;

      const originalOtherDesignations = Array.isArray(originalIAData?.other_designations)
        ? originalIAData.other_designations
        : [];

      originalOtherDesignations.forEach((original) => {
        if (
          original.source === "PD" &&
          original.candidatedesignationsid !== "" &&
          !currentOtherNamesLower.has((original.designation || "").trim().toLowerCase())
        ) {
          section3Deleted.push({
            candidatedesignationsid: original.candidatedesignationsid,
          });
        }
      });
    } else {
      const pdCriaByAbbr = {};
      pdCriaItems.forEach((d) => {
        const m = (d.designationdescription || "").match(/\(([^)]+)\)$/);
        if (m) pdCriaByAbbr[m[1].toUpperCase()] = d;
      });

      Object.keys(state.designations).forEach((id) => {
        const abbr = (state.designations[id]?.abbr || "").toUpperCase();
        const pdItem = pdCriaByAbbr[abbr];
        if (!pdItem) return;
        if (!state.designations[id].checked) {
          section2Deleted.push(pdItem);
        }
      });

      const currentOtherRows = state.otherDesignations.filter((row) =>
        (row.designation || "").trim()
      );

      pdNonCriaItems.forEach((d) => {
        const stillPresent = currentOtherRows.some(
          (row) =>
            row.designation.trim().toLowerCase() ===
            (d.designationdescription || "").toLowerCase()
        );
        if (!stillPresent) section3Deleted.push(d);
      });
    }

    skipOnLoadRef.current = true;
    setShowSaveLoader(true);

    try {
      for (const item of section2Deleted) {
        if (item.candidatedesignationsid) {
          await deleteSingleProfessionalDesignation(props.subId, item.candidatedesignationsid);
        }
      }

      section2Deleted.forEach((item) => {
        const m = (item.designationdescription || "").match(/\(([^)]+)\)$/);
        const abbr = m ? m[1].toUpperCase() : null;
        if (!abbr) return;
        const matchingId = Object.keys(state.designations).find(
          (id) => (state.designations[id]?.abbr || "").toUpperCase() === abbr
        );
        if (matchingId) {
          dispatch({
            type: "updateDesignation",
            id: matchingId,
            payload: { checked: false, date: "", error: false, errorText: "" },
          });
        }
      });

      for (const item of section3Deleted) {
        if (item.candidatedesignationsid) {
          await deleteSingleProfessionalDesignation(props.subId, item.candidatedesignationsid);
        }
      }

      const deletedCandidateIds = new Set([
        ...section2Deleted.map((i) => i.candidatedesignationsid),
        ...section3Deleted.map((i) => i.candidatedesignationsid),
      ]);
      if (deletedCandidateIds.size > 0 && props.saveProfDesData) {
        const updatedProfDesignations = pdDesignations.filter(
          (d) => !deletedCandidateIds.has(d.candidatedesignationsid)
        );
        props.saveProfDesData({
          ...props.profDesData,
          professionalDesignations: updatedProfDesignations,
        });
      }
    } catch (error) {
      console.error("Error syncing PD records:", error);
    }

    try {
      await insertProfessionalDesignationsIA(props.subId, payload);
      props.isFormComplete(true);
      props.hasCurrentFormChanged(false);
      if (props.isCRIA || props?.isIA) {
        if (props.updateProfDesCRIACompleteInd) props.updateProfDesCRIACompleteInd(true);
        props.saveprofDesCriaData(payload, "CRIA");
        isAgentEmeritusAndRetiredBrokerContract(props.contracttypedesc)
          ? props.toggleForm(FORM_ID.DIRECT_DEPOSIT)
          : props.toggleForm(FORM_ID.EMPLOYMENT_HISTORY);
        return;
      }
      props.saveprofDesCriaData(payload);
      props.toggleForm(FORM_ID.REVIEW_SIGN_1);
    } catch (error) {
      console.error("Error during API call:", error);
    } finally {
      setShowSaveLoader(false);
    }
  };

  return (
    <>
      <div className="professional-designations-form">
        <h1 className="mm-spacing">
          Professional Registrations or Designations
        </h1>
        <h4>Registrations</h4>
        <div className="registrations-list">
          {Object.keys(state.registrations).map((id) => (
            <div key={id} className="custom-control custom-checkbox">
              <input
                type="checkbox"
                id={id}
                checked={state.registrations[id]?.checked || false}
                onChange={(e) => {
                  props.isFormComplete(false);
                  props.hasCurrentFormChanged(true);
                  setHasFormChanged(true);
                  dispatch({
                    type: "updateRegistration",
                    id,
                    payload: { checked: e.target.checked },
                  });
                  if (!e.target.checked) {
                    dispatch({ type: "updateRegistration", id, payload: { date: "" } });
                    dispatch({
                      type: "onBlur",
                      fieldName: id,
                      target: "registrations",
                      errorText: "",
                      hasError: false,
                    });
                  }
                }}
                className={`custom-control-input ${
                  state.professionalDesignationError ? "is-invalid" : ""
                }`}
              />
              <label className="custom-control-label" htmlFor={id}>
                <strong>{state.registrations[id]?.label}</strong>
              </label>
              {state.registrations[id]?.checked && (
                <div className="row date-wrapper">
                  <div className="form-group col-md-6">
                    <label htmlFor={`date-${id}`}>Date acquired</label>
                    <InputMask
                      id={`date-${id}`}
                      mask="99/9999"
                      maskChar="_"
                      className={`form-control${state.registrations[id]?.error ? " is-invalid" : ""}`}
                      style={{ maxWidth: "150px", minWidth: "80px" }}
                      placeholder="MM/YYYY"
                      value={state.registrations[id]?.date || ""}
                      onBlur={(e) => {
                        const errorMsg = getDateErrorMsg(e.target.value);
                        dispatch({
                          type: "onBlur",
                          fieldName: id,
                          target: "registrations",
                          errorText: errorMsg,
                          hasError: !!errorMsg,
                        });
                      }}
                      onChange={(e) => {
                        props.isFormComplete(false);
                        props.hasCurrentFormChanged(true);
                        setHasFormChanged(true);
                        dispatch({
                          type: "updateRegistration",
                          id,
                          payload: { date: e.target.value },
                        });
                        dispatch({ type: "markChanged", id, target: "registrations" });
                      }}
                      autoComplete="off"
                    />
                    {state.registrations[id]?.error && state.registrations[id]?.errorText && (
                      <div className="invalid-feedback">
                        {state.registrations[id]?.errorText}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="align-self-stretch row">
          <div className="col-1"></div>
          <div
            className="card--outline col-9"
            style={{ backgroundColor: "#F3F4F6", borderColor: "#FFFFFF" }}
          >
            <div className="card-body">
              <div className="row">
                <h4 className="title col-12">Designations</h4>
              </div>
              <div className="row" style={{ display: "flex" }}>
                <p className="mm-media-object col-8">
                  In place of the Series 65 or Series 66 exam, one of the
                  following designations may qualify you to become RIA
                  registered:
                </p>
                <p className="mm-media-object col-4">
                  <ul>
                    <li>CFA</li>
                    <li>ChFC</li>
                    <li>CIC</li>
                    <li>CFP</li>
                    <li>PFS</li>
                  </ul>
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="designations-list">
          <div className="row">
            <h4> Other Designation(s) held: &nbsp; &nbsp;</h4>
            <p style={{ marginTop: "4px" }}>
              Indicate if you hold one or more of the following designations.
            </p>
          </div>
          {Object.keys(state.designations).map((id) => (
            <div key={id} className="custom-control custom-checkbox">
              <input
                type="checkbox"
                id={id}
                checked={state.designations[id]?.checked || false}
                disabled={state.designations[id]?.disabled || false}
                onChange={(e) => {
                  if (state.designations[id]?.disabled) return;
                  props.isFormComplete(false);
                  props.hasCurrentFormChanged(true);
                  setHasFormChanged(true);
                  dispatch({
                    type: "updateDesignation",
                    id,
                    payload: { checked: e.target.checked },
                  });
                  if (!e.target.checked) {
                    dispatch({ type: "updateDesignation", id, payload: { date: "" } });
                    dispatch({
                      type: "onBlur",
                      fieldName: id,
                      target: "designations",
                      errorText: "",
                      hasError: false,
                    });
                  }
                }}
                className={`custom-control-input ${
                  state.professionalDesignationError ? "is-invalid" : ""
                }`}
              />
              <label className="custom-control-label" htmlFor={id}>
                <strong>{state.designations[id]?.label}</strong>
              </label>
              {state.designations[id]?.checked && (
                <div className="row date-wrapper">
                  <div className="form-group col-md-6">
                    <label htmlFor={`date-${id}`}>Date acquired</label>
                    <InputMask
                      id={`date-${id}`}
                      mask="99/9999"
                      maskChar="_"
                      className={
                        state.designations[id]?.error
                          ? "form-control is-invalid"
                          : "form-control"
                      }
                      style={{ maxWidth: "150px", minWidth: "80px" }}
                      placeholder="MM/YYYY"
                      value={state.designations[id]?.date || ""}
                      disabled={
                        state.designations[id]?.disabled ||
                        state.designations[id]?.dateDisabled ||
                        false
                      }
                      onBlur={(e) => {
                        if (
                          state.designations[id]?.disabled ||
                          state.designations[id]?.dateDisabled
                        ) return;
                        const errorMsg = getDateErrorMsg(e.target.value);
                        dispatch({
                          type: "onBlur",
                          fieldName: id,
                          target: "designations",
                          errorText: errorMsg,
                          hasError: !!errorMsg,
                        });
                      }}
                      onChange={(e) => {
                        if (
                          state.designations[id]?.disabled ||
                          state.designations[id]?.dateDisabled
                        ) return;
                        props.isFormComplete(false);
                        props.hasCurrentFormChanged(true);
                        setHasFormChanged(true);
                        dispatch({
                          type: "updateDesignation",
                          id,
                          payload: { date: e.target.value },
                        });
                        dispatch({ type: "markChanged", id, target: "designations" });
                      }}
                      autoComplete="off"
                    />
                    {state.designations[id]?.error && (
                      <div className="invalid-feedback">
                        {state.designations[id]?.errorText || "Please enter a valid date"}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="designations-list">
          <div className="row" style={{ fontWeight: "bold", marginBottom: "8px" }}>
            <div className="col-md-7">Other designation(s)</div>
            <div className="col-md-2">Date acquired</div>
            <div className="col-md-2"></div>
          </div>
          {state.otherDesignations
            .slice(
              (otherDesignationsPage - 1) * PAGE_SIZE,
              otherDesignationsPage * PAGE_SIZE
            )
            .map((row, index) => {
              const realIndex = (otherDesignationsPage - 1) * PAGE_SIZE + index;
              return (
                <React.Fragment key={realIndex}>
                  <div className="designation-wrapper row d-flex align-items-center mb-2">
                    <div className="col-md-7">
                      <input
                        type="text"
                        id={`designation-${realIndex}`}
                        className="form-control"
                        value={row.designation}
                        disabled={row.disabled || row.fromPD || false}
                        onChange={(e) => {
                          if (row.disabled || row.fromPD) return;
                          props.isFormComplete(false);
                          props.hasCurrentFormChanged(true);
                          setHasFormChanged(true);
                          handleInputChange(realIndex, "designation", e.target.value);
                        }}
                        placeholder="Enter designation"
                      />
                      {row.error && !row.designation && (
                        <div className="invalid-feedback">Designation is required</div>
                      )}
                    </div>
                    <div className="col-md-2">
                      <InputMask
                        type="text"
                        id={`date-${realIndex}`}
                        mask="99/9999"
                        maskChar="_"
                        className={`form-control ${row.error && !row.designation ? "is-invalid" : ""}`}
                        style={{ maxWidth: "180px", minWidth: "140px" }}
                        value={row.date}
                        disabled={row.disabled || row.fromPD || false}
                        onChange={(e) => {
                          if (row.disabled || row.fromPD) return;
                          props.isFormComplete(false);
                          props.hasCurrentFormChanged(true);
                          setHasFormChanged(true);
                          handleInputChange(realIndex, "date", e.target.value);
                          dispatch({
                            type: "updateOtherDesignation",
                            index: realIndex,
                            field: "changed",
                            value: true,
                          });
                        }}
                        placeholder="MM/YYYY"
                        onBlur={(e) => {
                          if (row.disabled || row.fromPD) return;
                          const errorMsg = getDateErrorMsg(e.target.value);
                          dispatch({
                            type: "updateOtherDesignation",
                            index: realIndex,
                            field: "error",
                            value: !!errorMsg,
                          });
                          dispatch({
                            type: "updateOtherDesignation",
                            index: realIndex,
                            field: "errorText",
                            value: errorMsg,
                          });
                        }}
                      />
                    </div>
                    <div className="col-md-2" style={{ marginLeft: "20px" }}>
                      {state.otherDesignations.length > 1 &&
                        realIndex !== state.otherDesignations.length - 1 && (
                          <span
                            className="icon-delete"
                            onClick={() => handleDeleteRow(realIndex)}
                            style={{ cursor: "pointer" }}
                          ></span>
                        )}
                      {realIndex === state.otherDesignations.length - 1 && (
                        <span
                          className="icon-add"
                          onClick={handleAddRow}
                          style={{ cursor: "pointer" }}
                        ></span>
                      )}
                    </div>
                  </div>
                  {row.error && row.errorText && (
                    <div className="row" style={{ marginTop: "-10px" }}>
                      <div className="col-md-7"></div>
                      <div
                        className="col-md-3 invalid-feedback"
                        style={{
                          display: "block",
                          minWidth: "150px",
                          whiteSpace: "nowrap",
                          textAlign: "left",
                          marginBottom: "10px",
                        }}
                      >
                        {row.errorText}
                      </div>
                      <div className="col-md-2"></div>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          <span
            id="personal_why_we_need_this"
            style={{ fontSize: "12px", color: "#555555" }}
          >
            &nbsp; Add a designation not displayed above
          </span>
          {nPages > 1 && (
            <Pagination
              currentPage={otherDesignationsPage}
              nPages={nPages}
              pageNumbers={pageNumbers}
              handleChange={handleOtherDesignationsPageChange}
            />
          )}
        </div>
      </div>

      <NavigationButtons
        onBack={handleShowBackModal}
        onNext={handleNext}
        isLoading={showSaveLoader}
        nxtbtnid={"prfdesgnext"}
        bckbtnid={"prfdesgbck"}
      />

      <LostDataModal
        isOpen={showBackModal}
        handleClose={handleCloseBackModal}
        handleProceed={handleBackClick}
      />
    </>
  );
};

export default ProfessionalDesignationsInvestmentAdvisor;
